import axios from "axios";
import React, { useRef, useState } from "react";
import Select from "react-select";
import { InventoryOperations } from "../utils/common";
import {
  ExclusionActionType,
  GzCompanyType,
  KaseyaInventoryItemType,
  OperationType,
} from "../utils/enums";

import warningDeleteAssociationPng from "../assets/images/warning_orange.svg";
import { toastNotification } from "../utils/apiFunctions";
import { HtmlEscapeUtils } from "../utils/HtmlEscapeUtils";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "white",
    minHeight: "32px",
    height: "32px",
    alignItems: "center",
    flexDirection: "row",
  }),
  placeholder: (styles) => ({ ...styles, margin: "0 0 0 8px" }),
  valueContainer: (styles) => ({
    ...styles,
    padding: "unset",
    height: "20px",
    alignItems: "center",
  }),
  singleValue: (styles) => ({ ...styles, margin: "0 0 0 8px" }),
  input: (styles) => ({ ...styles, margin: "none", padding: "0" }),
  clearIndicator: (styles) => ({ ...styles, padding: "0" }),
  dropdownIndicator: (styles) => ({ ...styles, padding: "0" }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      borderBottomWidth:
        data.value === InventoryOperations.deleteAssociationAction.value
          ? "1px"
          : "",
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? "#F2F2F2"
        : isFocused
        ? "#F2F2F2"
        : undefined,
      color: isDisabled
        ? "#B3B3B3"
        : isSelected
        ? "#4D4D4D"
        : isFocused
        ? "4D4D4D"
        : undefined,
      cursor: isDisabled ? undefined : "pointer",
    };
  },
};

const addOperation = (
  operationType,
  rmmCompanyName,
  rmmCompanyId,
  rmmItemType,
  rmmParentId = null,
  gzCompanyIdToAssociateWith = null,
  gzCompanyNameToAssociateWith = null,
) => {
  return {
      operationType: operationType,
      itemType: 1,
      rmmItemType: rmmItemType,
      contextType: rmmItemType,
      rmmParentId: rmmParentId,
      itemId: rmmCompanyId,
      itemName: rmmCompanyName,
      AssociateWithGravityzoneId: gzCompanyIdToAssociateWith,
      newGzCompanyName: gzCompanyNameToAssociateWith,
      createGroups: true,
      packageConfigurationLevel: 0,
      featuresSyncMode: 0,
    };
};

const GzCompanySelect = ({
  index,
  rmmCompanyInfo,
  rmmCompanyInfoList,
  actionsToPerform,
  updateActionsToPerform,
  handleAddingChangedRows,
  handleRemovingChangedRows,
  handleExcludedItems,
}) => {
  const [state, setState] = useState({
    options: [],
    isLoading: false,
  });

  let companySelectRef = useRef();

  const renderDialogFooterButtons = () => {
    const footer = document.createElement("div");

    footer.innerHTML = `
        <gz-button id="confirmBtn" type="full" color="negative">Confirm</gz-button>
        <gz-button id="cancelBtn" type="outline" color="secondary">Cancel</gz-button>
    `;
    return footer;
  };

  let dialog;
  const deleteDialogBody = (rowInfo, choice) => {
    const dialogBody = document.createElement("div");

    const gzCompanyName = HtmlEscapeUtils.htmlEncode(rmmCompanyInfo.companyDetails?.name);
    const vsaEntityName = HtmlEscapeUtils.htmlEncode(rowInfo.rmmCompanyName);

    dialogBody.innerHTML = `
        <div>
          The current association between the VSA X organization <b>${vsaEntityName}</b> and the GravityZone company <b>${
      gzCompanyName === undefined ? "" : gzCompanyName
    }</b> will be deleted, and all services disabled.<br>
          The GravityZone company can then be reused for a new mapping.
        </div>
        <div style='margin-top: 28px'>
          <div>
            <b>Optional:</b>
          </div>
          <gz-checkbox id='deleteCheckbox' gz-form-input id='exclude-sync-checkbox' label=''>
            <gz-text fontsize="14">Exclude organization from synchronization</gz-text>
          </gz-checkbox>
        </div>
        <div style='display: flex; align-items: center; margin-top: 18px; gap: 8px'>
          <img class='' src=${warningDeleteAssociationPng} alt='warningDeleteAsocPng'>
          <span>Warning:</span>
        </div>
        <div>
          All child associations will also be removed.<br>
          To finalize the action, click <b>Save changes</b> on the page.
        </div>
    `;

    const dialogFooter = renderDialogFooterButtons();
    dialogFooter.querySelector("#confirmBtn").addEventListener("click", () => {
      dialog.dismiss();
      dialog.emitDialogEvent("confirm");
    });

    dialogFooter.querySelector("#cancelBtn").addEventListener("click", () => {
      dialog.dismiss();
      dialog.emitDialogEvent("cancel");
    });

    const dialogConfig = {
      headline: "Delete association?",
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };

    dialog =
      window.nirvana.dependencies.services.buildDialogElement(dialogConfig);
    dialog.addEventListener("confirm", () => {
      const isExcludeFromSyncSelected =
        dialogBody.querySelector("#deleteCheckbox").checked;

      if (isExcludeFromSyncSelected) {
        handleExcludedItems(
          rmmCompanyInfo.rmmCompanyId,
          ExclusionActionType.EXCLUDE
        );
      }

      updateActionsToPerform([
        ...actionsToPerform,
        addOperation(
          choice?.value,
          rmmCompanyInfo.rmmCompanyName,
          rmmCompanyInfo.rmmCompanyId,
          rmmCompanyInfo.rmmType,
          rmmCompanyInfo.parentOrganizationId
        ),
      ]);
    });
    dialog.addEventListener("cancel", () => {
      handleRemovingChangedRows(index);
    });
  };

  const handleSelection = (choice) => {
    // Allow multiple actions only on children under the same parentOrg
    let isActionDenied = false;
    let existingParentOrgIds = actionsToPerform
      .filter((a) => a.rmmItemType === KaseyaInventoryItemType.ORGANIZATION)
      .map((i) => parseInt(i.itemId));

    let existingParentOrgIdsOfSites = actionsToPerform
      .filter((a) => a.rmmItemType === KaseyaInventoryItemType.SITE)
      .map((i) => parseInt(i.rmmParentId));

    // a single parentId in operations and target is site and parent is different
    if (
      existingParentOrgIds.length === 1 &&
      rmmCompanyInfo.rmmType === KaseyaInventoryItemType.SITE &&
      rmmCompanyInfo.parentOrganizationId !== existingParentOrgIds[0]
    ) {
      isActionDenied = true;
    }
    // multiple parentIds in operations and target is site
    if (
      existingParentOrgIds.length > 1 &&
      rmmCompanyInfo.rmmType === KaseyaInventoryItemType.SITE
    ) {
      isActionDenied = true;
    }
    // existing actions on children but current target is site that has different parentId
    if (
      existingParentOrgIdsOfSites.length > 0 &&
      rmmCompanyInfo.rmmType === KaseyaInventoryItemType.SITE &&
      !existingParentOrgIdsOfSites.includes(rmmCompanyInfo.parentOrganizationId)
    ) {
      isActionDenied = true;
    }
    // existing actions on children with same parentId and current target is org with a different id
    if (
      existingParentOrgIdsOfSites.length > 0 &&
      rmmCompanyInfo.parentOrganizationId === null &&
      !existingParentOrgIdsOfSites.includes(
        parseInt(rmmCompanyInfo.rmmCompanyId)
      )
    ) {
      isActionDenied = true;
    }
    if (isActionDenied) {
      toastNotification(
        "Cannot manage associations at different levels simultaneously.",
        "warning",
        true
      );
      return;
    }
    // End check of multiple actions on different parents or children under different parents
    if (
      choice?.value === InventoryOperations.deleteAssociationAction.value &&
      rmmCompanyInfo.isDestinationDeleted
    ) {
      let existingDeleteAssociationAction = actionsToPerform.find(
        (a) =>
          parseInt(a.itemId) === parseInt(rmmCompanyInfo.rmmCompanyId) &&
          a.operationType === OperationType.DELETE_ASSOCIATION
      );
      if (existingDeleteAssociationAction !== undefined) {
        return;
      }
      deleteDialogBody(rmmCompanyInfo, choice);
    }
    // delete action and there's no existing mapping - do nothing
    else if (
      choice?.value === InventoryOperations.deleteAssociationAction.value &&
      rmmCompanyInfo.gravityZoneCompanyId === null
    ) {
      return;
    }
    // delete action of an existing mapping
    else if (
      choice?.value === InventoryOperations.deleteAssociationAction.value &&
      rmmCompanyInfo.gravityZoneCompanyId
    ) {
      let existingDeleteAssociationAction = actionsToPerform.find(
        (a) =>
          parseInt(a.itemId) === parseInt(rmmCompanyInfo.rmmCompanyId) &&
          a.operationType === OperationType.DELETE_ASSOCIATION
      );
      if (existingDeleteAssociationAction !== undefined) {
        return;
      }
      deleteDialogBody(rmmCompanyInfo, choice);
    }
    // clear existing option if a previous one has been selected
    else if (choice === null) {
      if (
        companySelectRef.current.state.selectValue[0].value ===
        InventoryOperations.deleteAssociationAction.value
      ) {
        handleExcludedItems(
          rmmCompanyInfo.rmmCompanyId,
          ExclusionActionType.INCLUDE
        );
      }
      updateActionsToPerform(
        actionsToPerform.filter(
          (a) => parseInt(a.itemId) !== parseInt(rmmCompanyInfo.rmmCompanyId)
        )
      );
      handleRemovingChangedRows(index);
      return;
    }
    // associate action logic
    else {
      let existingAction = actionsToPerform.find(
        (a) => parseInt(a.itemId) === parseInt(rmmCompanyInfo.rmmCompanyId)
      );
      let indexOfExistingAction = actionsToPerform.indexOf(existingAction);

      // update a previous mapping option with a new one
      if (indexOfExistingAction !== -1) {
        if (
          companySelectRef.current.state.selectValue[0].value ===
          InventoryOperations.deleteAssociationAction.value
        ) {
          handleExcludedItems(
            rmmCompanyInfo.rmmCompanyId,
            ExclusionActionType.INCLUDE
          );
        }

        let updatedActionsToPerform = Array.from(actionsToPerform);
        updatedActionsToPerform[indexOfExistingAction] = addOperation(
          InventoryOperations.associateAction.value,
          rmmCompanyInfo.rmmCompanyName,
          rmmCompanyInfo.rmmCompanyId,
          rmmCompanyInfo.rmmType,
          rmmCompanyInfo.parentOrganizationId,
          choice?.value,
          choice?.label
        );
        updateActionsToPerform(updatedActionsToPerform);
        return;
      }
      // associate action
      updateActionsToPerform([
        ...actionsToPerform,
        addOperation(
          InventoryOperations.associateAction.value,
          rmmCompanyInfo.rmmCompanyName,
          rmmCompanyInfo.rmmCompanyId,
          rmmCompanyInfo.rmmType,
          rmmCompanyInfo.parentOrganizationId,
          choice?.value,
          choice?.label
        ),
      ]);
    }
    handleAddingChangedRows(index);
  };

  const filterOptionsBasedOnActiveActions = (retrievedOptions) => {
    let filteredOptions = [];

    // type is site and the parent is mapped -> show only gz companies under the parent mapped entity
    if (rmmCompanyInfo.rmmType === KaseyaInventoryItemType.SITE) {
      let parentIsToDeassociate = actionsToPerform.find(
        (a) =>
          parseInt(a.itemId) ===
            parseInt(rmmCompanyInfo.parentOrganizationId) &&
          a.operationType === InventoryOperations.deleteAssociationAction.value
      );

      // parent set to delete association -> show only the delete option on childs dropdown
      if (parentIsToDeassociate) {
        let deleteAssociationEntry = retrievedOptions.find(
          (op) => op.value === InventoryOperations.deleteAssociationAction.value
        );
        filteredOptions.push(deleteAssociationEntry);
      }
      // show the filtered listed received from backend
      else {
        filteredOptions = retrievedOptions.filter(
          (op) =>
            !actionsToPerform.find(
              (a) =>
                a.operationType === InventoryOperations.associateAction.value &&
                a.associateWithGravityzoneId === op.value
            )
        );
      }
    }
    // type is org - filter the options list by removing items from the active actions list
    else {
      filteredOptions = retrievedOptions.filter(
        (op) =>
          !actionsToPerform.find(
            (a) =>
              a.operationType === InventoryOperations.associateAction.value &&
              a.associateWithGravityzoneId === op.value
          )
      );
    }
    return filteredOptions;
  };

  const handleLoadOptions = async (input, callback) => {
    let options = [];
    let parentOrg;
    let parentGzCompanyId;

    if (rmmCompanyInfo.parentOrganizationId) {
      parentOrg = rmmCompanyInfoList.find(
        (r) => parseInt(r.rmmCompanyId) === rmmCompanyInfo.parentOrganizationId
      );
      if (
        parentOrg &&
        parentOrg.companyDetails.type === GzCompanyType.CUSTOMER
      ) {
        options.push({
          value: InventoryOperations.deleteAssociationAction.value,
          label: InventoryOperations.deleteAssociationAction.label,
          isDisabled: true,
        });

        setState({
          options: options,
          isLoading: false,
        });

        return;
      }
    }

    if (parentOrg !== undefined) {
      const activeParentAction = actionsToPerform.find(
        (a) =>
          parseInt(a.itemId) ===
            parseInt(parentOrg.rmmCompanyId) &&
          a.operationType === InventoryOperations.associateAction.value
      );

      if (activeParentAction !== null && activeParentAction !== undefined) {
        parentGzCompanyId = activeParentAction.associateWithGravityzoneId;
      } else {
        parentGzCompanyId = parentOrg.gravityZoneCompanyId;
      }
    }

    let url =
      rmmCompanyInfo.rmmType === KaseyaInventoryItemType.ORGANIZATION
        ? process.env.REACT_APP_KASEYA_STUB_API_URL +
          "/api/companies/availablegzcompanies"
        : process.env.REACT_APP_KASEYA_STUB_API_URL +
          `/api/companies/availablegzcompanies?parentGzCompanyId=${parentGzCompanyId}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    });

    let childrenAssociationActions = actionsToPerform.filter(
      (a) =>
        a.rmmParentId === parseInt(rmmCompanyInfo.rmmCompanyId) &&
        a.operationType === InventoryOperations.associateAction.value
    );

    const isDeleteAssociationDisabled = () => {
      let isInactive = true;
      if (
        rmmCompanyInfo.gravityZoneCompanyId !== null &&
        childrenAssociationActions.length === 0
      ) {
        isInactive = false;
      }

      return isInactive;
    };

    options.push({
      value: InventoryOperations.deleteAssociationAction.value,
      label: InventoryOperations.deleteAssociationAction.label,
      isDisabled: isDeleteAssociationDisabled(),
    });

    response.data.map((option) => {
      const newOption = {
        value: option.id,
        label: option.name,
        color: "black",
      };
      options.push(newOption);
    });

    setState({
      options: filterOptionsBasedOnActiveActions(options),
      isLoading: false,
    });
  };

  const maybeLoadOptions = () => {
    setState({ isLoading: true });
    handleLoadOptions();
  };

  const isDisabledDueToParentNotAssociated = (rmmCompanyInfo) => {
    let parent = rmmCompanyInfoList.find(
      (a) => parseInt(a.rmmCompanyId) === rmmCompanyInfo.parentOrganizationId
    );
    return !!(parent && !parent.gravityZoneCompanyId && !parent.companyDetails);
  };

  const isAssociationForDeletion = (association) => {
    return (
      association.operationType ===
        InventoryOperations.deleteAssociationAction.value &&
      parseInt(association.itemId) === parseInt(rmmCompanyInfo.rmmCompanyId)
    );
  };

  const isSetForNewAssociation = (association) => {
    return (
      association.operationType === InventoryOperations.associateAction.value &&
      parseInt(association.itemId) === parseInt(rmmCompanyInfo.rmmCompanyId)
    );
  };

  const getValue = () => {
    let valueToReturn;
    let elementForDeleteAssociation = actionsToPerform?.find(
      isAssociationForDeletion
    );
    let elementWithNewAssociation = actionsToPerform?.find(
      isSetForNewAssociation
    );
    if (elementForDeleteAssociation) {
      valueToReturn = {
        value: InventoryOperations.deleteAssociationAction.value,
        label: InventoryOperations.deleteAssociationAction.label,
      };
    } else if (elementWithNewAssociation) {
      valueToReturn = {
        value: elementWithNewAssociation.associateWithGravityzoneId,
        label: elementWithNewAssociation.newGzCompanyName,
      };
    } else {
      if (
        rmmCompanyInfo.rmmType === KaseyaInventoryItemType.SITE &&
        rmmCompanyInfo.gravityZoneCompanyId
      ) {
        let parentIsToDeassociate = actionsToPerform.find(
          (a) =>
            parseInt(a.itemId) ===
              parseInt(rmmCompanyInfo.parentOrganizationId) &&
            a.operationType ===
              InventoryOperations.deleteAssociationAction.value
        );

        if (parentIsToDeassociate) {
          valueToReturn = {
            value: InventoryOperations.deleteAssociationAction.value,
            label: InventoryOperations.deleteAssociationAction.label,
          };
        } else {
          valueToReturn = {
            value: rmmCompanyInfo.companyDetails.name,
            label: rmmCompanyInfo.companyDetails.name,
          };
        }
      } else {
        valueToReturn = rmmCompanyInfo.gravityZoneCompanyId
          ? {
              value: rmmCompanyInfo.companyDetails.name,
              label: rmmCompanyInfo.companyDetails.name,
            }
          : null;
      }
    }

    return valueToReturn;
  };

  const isDisabled = () => {
    const isDisabledParentNotAssociated =
      rmmCompanyInfo.rmmType === 2
        ? isDisabledDueToParentNotAssociated(rmmCompanyInfo)
        : false;
    return isDisabledParentNotAssociated;
  };

  return (
    <div className="h-full flex items-center">
      <Select
        className="gzCompanySelect w-[240px]"
        ref={companySelectRef}
        searchable={true}
        onBlurResetsInput={false}
        onCloseResetsInput={false}
        menuPlacement={
          index > rmmCompanyInfoList.length / 2 - 1 && index >= 6
            ? "top"
            : "bottom"
        }
        closeMenuOnSelect
        onChange={(choice) => handleSelection(choice)}
        labelKey="label"
        value={getValue()}
        placeholder="Select an option"
        valuekey="value"
        autoload={false}
        isClearable
        isDisabled={isDisabled()}
        isLoading={state.isLoading}
        options={state.options}
        onFocus={maybeLoadOptions}
        styles={colourStyles}
      />
    </div>
  );
};

export default GzCompanySelect;
